import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Chip, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Stack, Divider, FormControlLabel, Switch, CircularProgress, Snackbar, Alert, IconButton, Tooltip,
} from '@mui/material';
import { Add, Refresh, Tune } from '@mui/icons-material';
import SubscriptionPlanService from '../../services/subscriptionPlan.service';

const TIERS = ['FREE', 'BASIC', 'PREMIUM', 'ENTERPRISE'];
const UNITS = ['MONTH', 'YEAR'];
const FEATURES = ['ORG_COUNT', 'TC_USER_COUNT', 'INVOICE_MONTHLY', 'WORK_ORDER_MONTHLY', 'QUOTATION_MONTHLY',
  'MAX_B2B_CONNECTIONS', 'MAX_B2B_CLIENTS'];
const EMPTY_PLAN = { name: '', tier: 'BASIC', description: '', display_order: 0, is_publicly_available: true, is_active: true };

const SubscriptionPlansCatalog = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [planForm, setPlanForm] = useState(null); // create/edit
  const [manage, setManage] = useState(null); // plan being managed
  const [priceForm, setPriceForm] = useState({ period_unit: 'MONTH', period_count: 1, price: '', currency: 'AED' });
  const [entForm, setEntForm] = useState({ feature_key: 'ORG_COUNT', limit_value: '', unlimited: false });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const load = useCallback(() => {
    setLoading(true);
    SubscriptionPlanService.list()
      .then((list) => setPlans(Array.isArray(list) ? list : []))
      .catch(() => notify('Failed to load plans', 'error'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const refreshManage = (planId) => SubscriptionPlanService.get(planId).then((p) => setManage(p));

  const savePlan = () => {
    const op = planForm.id ? SubscriptionPlanService.update(planForm.id, planForm) : SubscriptionPlanService.create(planForm);
    op.then(() => { notify('Plan saved'); setPlanForm(null); load(); })
      .catch((e) => notify(e?.response?.data?.message || 'Save failed', 'error'));
  };

  const deactivatePlan = (id) => {
    SubscriptionPlanService.deactivate(id).then(() => { notify('Plan deactivated'); load(); }).catch(() => notify('Failed', 'error'));
  };

  const addPrice = () => {
    SubscriptionPlanService.addPrice(manage.id, {
      period_unit: priceForm.period_unit, period_count: Number(priceForm.period_count) || 1,
      price: Number(priceForm.price) || 0, currency: priceForm.currency,
    }).then(() => { notify('Price added'); setPriceForm({ period_unit: 'MONTH', period_count: 1, price: '', currency: 'AED' }); refreshManage(manage.id); load(); })
      .catch((e) => notify(e?.response?.data?.message || 'Failed', 'error'));
  };

  const removePrice = (priceId) => {
    SubscriptionPlanService.removePrice(priceId).then(() => { notify('Price removed'); refreshManage(manage.id); load(); }).catch(() => notify('Failed', 'error'));
  };

  const setEntitlement = () => {
    SubscriptionPlanService.setEntitlement(manage.id, {
      feature_key: entForm.feature_key,
      limit_value: entForm.unlimited ? null : (Number(entForm.limit_value) || 0),
      unlimited: entForm.unlimited,
    }).then(() => { notify('Entitlement set'); refreshManage(manage.id); }).catch((e) => notify(e?.response?.data?.message || 'Failed', 'error'));
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5">Subscription Plans</Typography>
          <Typography variant="caption" color="text.secondary">Manage the plan catalog, durations and limits.</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh"><IconButton onClick={load}><Refresh /></IconButton></Tooltip>
          <Button variant="contained" startIcon={<Add />} onClick={() => setPlanForm({ ...EMPTY_PLAN })}>Add plan</Button>
        </Stack>
      </Stack>

      <Card>
        <CardContent sx={{ p: 0 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell><TableCell>Tier</TableCell><TableCell>Prices</TableCell>
                  <TableCell>Public</TableCell><TableCell>Status</TableCell><TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {plans.map((p) => (
                  <TableRow key={p.id} hover>
                    <TableCell>{p.name}</TableCell>
                    <TableCell>{p.tier}</TableCell>
                    <TableCell>{(p.prices || []).length}</TableCell>
                    <TableCell>{p.is_publicly_available ? 'Yes' : 'No'}</TableCell>
                    <TableCell><Chip size="small" label={p.is_active ? 'Active' : 'Inactive'} color={p.is_active ? 'success' : 'default'} /></TableCell>
                    <TableCell align="right">
                      <Tooltip title="Manage prices & limits"><IconButton size="small" onClick={() => setManage(p)}><Tune fontSize="small" /></IconButton></Tooltip>
                      <Button size="small" onClick={() => setPlanForm({ id: p.id, name: p.name, tier: p.tier, description: p.description || '', display_order: p.display_order || 0, is_publicly_available: p.is_publicly_available, is_active: p.is_active })}>Edit</Button>
                      {p.is_active && <Button size="small" color="error" onClick={() => deactivatePlan(p.id)}>Deactivate</Button>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Create/edit plan */}
      <Dialog open={!!planForm} onClose={() => setPlanForm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{planForm?.id ? 'Edit plan' : 'Add plan'}</DialogTitle>
        <DialogContent>
          {planForm && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField label="Name" value={planForm.name} onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })} />
              <TextField select label="Tier" value={planForm.tier} onChange={(e) => setPlanForm({ ...planForm, tier: e.target.value })}>
                {TIERS.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
              <TextField label="Description" multiline minRows={2} value={planForm.description} onChange={(e) => setPlanForm({ ...planForm, description: e.target.value })} />
              <TextField label="Display order" type="number" value={planForm.display_order} onChange={(e) => setPlanForm({ ...planForm, display_order: Number(e.target.value) })} />
              <FormControlLabel control={<Switch checked={!!planForm.is_publicly_available} onChange={(e) => setPlanForm({ ...planForm, is_publicly_available: e.target.checked })} />} label="Publicly available" />
              <FormControlLabel control={<Switch checked={!!planForm.is_active} onChange={(e) => setPlanForm({ ...planForm, is_active: e.target.checked })} />} label="Active" />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlanForm(null)}>Cancel</Button>
          <Button variant="contained" onClick={savePlan}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Manage prices + entitlements */}
      <Dialog open={!!manage} onClose={() => setManage(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{manage?.name} — prices & limits</DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" gutterBottom>Prices</Typography>
          <Stack spacing={0.5} sx={{ mb: 1 }}>
            {(manage?.prices || []).map((pr) => (
              <Stack key={pr.id} direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2">{pr.period_count} {String(pr.period_unit).toLowerCase()} · {pr.price} {pr.currency}</Typography>
                <Button size="small" color="error" onClick={() => removePrice(pr.id)}>Remove</Button>
              </Stack>
            ))}
            {(manage?.prices || []).length === 0 && <Typography variant="caption" color="text.secondary">No prices (free plan)</Typography>}
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap">
            <TextField select size="small" label="Unit" value={priceForm.period_unit} sx={{ width: 110 }}
              onChange={(e) => setPriceForm({ ...priceForm, period_unit: e.target.value })}>
              {UNITS.map((u) => <MenuItem key={u} value={u}>{u}</MenuItem>)}
            </TextField>
            <TextField size="small" label="Count" type="number" value={priceForm.period_count} sx={{ width: 80 }}
              onChange={(e) => setPriceForm({ ...priceForm, period_count: e.target.value })} />
            <TextField size="small" label="Price" type="number" value={priceForm.price} sx={{ width: 110 }}
              onChange={(e) => setPriceForm({ ...priceForm, price: e.target.value })} />
            <Button variant="outlined" onClick={addPrice}>Add price</Button>
          </Stack>

          <Divider sx={{ my: 1.5 }} />
          <Typography variant="subtitle2" gutterBottom>Entitlements</Typography>
          <Stack spacing={0.5} sx={{ mb: 1 }}>
            {(manage?.entitlements || []).map((e) => (
              <Stack key={e.feature_key} direction="row" justifyContent="space-between">
                <Typography variant="body2">{e.feature_key}</Typography>
                <Typography variant="body2" color="text.secondary">{e.unlimited ? 'Unlimited' : e.limit_value}</Typography>
              </Stack>
            ))}
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <TextField select size="small" label="Feature" value={entForm.feature_key} sx={{ minWidth: 180 }}
              onChange={(e) => setEntForm({ ...entForm, feature_key: e.target.value })}>
              {FEATURES.map((f) => <MenuItem key={f} value={f}>{f}</MenuItem>)}
            </TextField>
            {!entForm.unlimited && (
              <TextField size="small" label="Limit" type="number" value={entForm.limit_value} sx={{ width: 100 }}
                onChange={(e) => setEntForm({ ...entForm, limit_value: e.target.value })} />
            )}
            <FormControlLabel control={<Switch checked={entForm.unlimited} onChange={(e) => setEntForm({ ...entForm, unlimited: e.target.checked })} />} label="Unlimited" />
            <Button variant="outlined" onClick={setEntitlement}>Set</Button>
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setManage(null)}>Close</Button></DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default SubscriptionPlansCatalog;
